import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LinkAdmin, listLinks } from "../api";
import type { NewLinkSeed } from "./AdminNewLink";

const INITIAL_PAGE = 10;
const NEXT_PAGE = 20;

interface Props {
  onCreateNew: () => void;
  onDuplicate: (seed: NewLinkSeed) => void;
}

function nextDuplicateTitle(baseTitle: string, allTitles: string[]): string {
  // Find existing "<baseTitle> <N>" suffixes (N >= 2, separated by single space)
  const prefix = `${baseTitle} `;
  const taken = new Set(allTitles);
  const existingNums: number[] = [];
  for (const t of allTitles) {
    if (!t.startsWith(prefix)) continue;
    const suffix = t.slice(prefix.length);
    if (/^\d+$/.test(suffix)) existingNums.push(parseInt(suffix, 10));
  }
  let n = existingNums.length > 0 ? Math.max(...existingNums) + 1 : 2;
  while (taken.has(`${baseTitle} ${n}`)) n++;
  return `${baseTitle} ${n}`;
}

function remainingDays(expiresAt?: string): number | undefined {
  if (!expiresAt) return undefined;
  const ms = new Date(expiresAt).getTime() - Date.now();
  if (Number.isNaN(ms) || ms <= 0) return undefined;
  return Math.max(1, Math.ceil(ms / 86400000));
}

export default function AdminLinks({ onCreateNew, onDuplicate }: Props) {
  const nav = useNavigate();
  const [links, setLinks] = useState<LinkAdmin[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    listLinks(INITIAL_PAGE, 0)
      .then((items) => {
        setLinks(items);
        setHasMore(items.length === INITIAL_PAGE);
      })
      .catch((e) => {
        if (String(e.message).includes("401")) nav("/admin/login");
        else setErr(e.message);
      });
  }, [nav]);

  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore || !links) return;
    setLoadingMore(true);
    try {
      const items = await listLinks(NEXT_PAGE, links.length);
      setLinks((prev) => [...(prev ?? []), ...items]);
      setHasMore(items.length === NEXT_PAGE);
    } catch (e: any) {
      setErr(e.message);
    } finally {
      setLoadingMore(false);
    }
  }, [hasMore, loadingMore, links]);

  // Infinite scroll: load next batch when the sentinel enters the viewport.
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !hasMore) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) loadMore();
      },
      { rootMargin: "200px" },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [loadMore, hasMore]);

  const copy = async (url: string, id: string) => {
    await navigator.clipboard.writeText(url);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 1500);
  };

  const waShare = (url: string, title: string, recipientName?: string) => {
    const who = recipientName ? `Hey ${recipientName}, ` : "";
    const msg = `${who}record me a video on "${title}" → ${url}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank");
  };

  const duplicate = (l: LinkAdmin) => {
    const allTitles = (links ?? []).map((x) => x.title);
    onDuplicate({
      title: nextDuplicateTitle(l.title, allTitles),
      text: l.text,
      mode: l.mode,
      recipient_name: l.recipient_name,
      recipient_email: l.recipient_email,
      max_duration_sec: l.max_duration_sec,
      expires_in_days: remainingDays(l.expires_at),
      briefing_video_s3_key: l.briefing_video_s3_key,
    });
  };

  return (
    <section className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-semibold">Shareable links</h2>
        <button
          onClick={onCreateNew}
          className="px-4 py-2 bg-charcoal text-ivory rounded-lg text-sm font-medium hover:bg-black transition"
        >
          + New link
        </button>
      </div>

      {err && <div className="text-red-600">{err}</div>}
      {!links && !err && <div className="text-concrete">Loading…</div>}

      {links && links.length === 0 && (
        <div className="text-concrete py-12 text-center bg-white rounded-xl">
          No links yet.{" "}
          <button
            onClick={onCreateNew}
            className="underline text-charcoal hover:text-black"
          >
            Create your first one
          </button>
          .
        </div>
      )}

      {links && links.length > 0 && (
        <ul className="space-y-3">
          {links.map((l) => (
            <li
              key={l.link_id}
              className="bg-white rounded-xl shadow-sm p-4 space-y-3"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <span className="font-bold text-lg">{l.title}</span>
                    {l.recipient_name && (
                      <span className="text-sm text-concrete">
                        → {l.recipient_name}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-wrap mt-1">
                    <span
                      className={`text-xs px-2 py-0.5 rounded-full ${
                        l.mode === "nominative"
                          ? "bg-lemonade/30 text-charcoal"
                          : "bg-stone text-concrete"
                      }`}
                    >
                      {l.mode}
                    </span>
                    {l.has_briefing && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-charcoal text-ivory">
                        ▶ briefing
                      </span>
                    )}
                    <span className="text-xs text-concrete">
                      {l.video_count} {l.video_count === 1 ? "reply" : "replies"}
                    </span>
                  </div>
                  <div className="text-sm text-concrete mt-2 line-clamp-2">
                    {l.text}
                  </div>
                  <div className="text-xs text-concrete mt-2">
                    {new Date(l.created_at).toLocaleString()}
                    {l.expires_at &&
                      ` · expires ${new Date(l.expires_at).toLocaleDateString()}`}
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2 pt-2 border-t border-stone">
                <input
                  readOnly
                  value={l.url}
                  className="flex-1 px-3 py-2 text-xs bg-ivory rounded border border-stone font-mono"
                  onFocus={(e) => e.currentTarget.select()}
                />
                <button
                  onClick={() => copy(l.url, l.link_id)}
                  className="px-3 py-2 text-sm bg-stone text-charcoal rounded hover:bg-ash transition"
                >
                  {copiedId === l.link_id ? "✓ Copied" : "Copy"}
                </button>
                <button
                  onClick={() => waShare(l.url, l.title, l.recipient_name)}
                  className="px-3 py-2 text-sm bg-[#25D366] text-white rounded hover:brightness-95 transition"
                >
                  WhatsApp
                </button>
                <button
                  onClick={() => duplicate(l)}
                  title="Duplicate this link"
                  className="px-3 py-2 text-sm bg-stone text-charcoal rounded hover:bg-ash transition"
                >
                  Duplicate
                </button>
              </div>

              {l.avatar_session_id && (
                <div className="pt-2 border-t border-stone/80">
                  <Link
                    to={`/avatar/sessions/${l.avatar_session_id}/share`}
                    className="inline-flex items-center justify-center w-full sm:w-auto px-4 py-2.5 rounded-lg bg-charcoal text-ivory text-sm font-semibold hover:opacity-95 transition"
                  >
                    Lancer la génération avatar
                  </Link>
                  <p className="text-xs text-concrete mt-2">
                    Ouvre la session après enregistrement : lien de capture, puis pipeline
                    Kling → TTS → lip-sync.
                  </p>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {links && links.length > 0 && hasMore && (
        <div
          ref={sentinelRef}
          className="text-center text-concrete text-sm py-6"
        >
          {loadingMore ? "Loading more…" : "Scroll for more"}
        </div>
      )}
      {links && links.length > 0 && !hasMore && (
        <div className="text-center text-concrete text-xs py-4 opacity-60">
          End of list
        </div>
      )}
    </section>
  );
}
